import { createSlice } from '@reduxjs/toolkit';
import {
  bulkAllocate,
  bulkDeAllocate,
  createRule,
  fetchAllocationRules,
  fetchAllocations,
  fetchAllocationStatus,
  fetchBulkAllocations,
  fetchBulkDeAllocations,
  fetchCriteriaAttributes,
  reallocateAllocations,
  fetchAllocationsMetaData,
} from '../actions/AllocationActions';
import { RootState } from '../store';
import {
  Allocation,
  AllocationMetaData,
  AllocationRuleViewModel,
  AllocationStatusViewModel,
  AllocationViewModel,
  BulkAllocationMeta,
  RuleCriteriaAttribute,
} from '../types/allocations';

export interface AllocationState {
  loading: boolean;
  loadingStatus: boolean;
  loadingBulkAllocations: boolean;
  loadingCriteriaAttributes: boolean;
  loadingAllocationRules: boolean;
  uploadingBulkFile: boolean;
  allocations: Allocation[];
  allocationsMetaData: AllocationMetaData[];
  criteriaAttributes: RuleCriteriaAttribute[];
  allocationRuleViewModels: AllocationRuleViewModel[];
  bulkAllocations: BulkAllocationMeta[];
  bulkDeAllocations: BulkAllocationMeta[];
  allocationViewModels: AllocationViewModel[];
  allocationStatus: AllocationStatusViewModel;
  page: number;
  allocationRulePage: number;
  totalItems: number;
  totalItemsOfMetaData: number;
  allocationRuleTotalItems: number;
  pageSize: number;
  allocationRulePageSize: number;
  error: string | null;
}

const initialState: AllocationState = {
  allocations: [],
  allocationsMetaData: [],
  allocationViewModels: [],
  allocationRuleViewModels: [],
  allocationStatus: {},
  loading: false,
  loadingStatus: false,
  loadingAllocationRules: false,
  uploadingBulkFile: false,
  error: null,
  page: 0,
  pageSize: 0,
  totalItems: 0,
  totalItemsOfMetaData: 0,
  allocationRulePage: 0,
  allocationRuleTotalItems: 0,
  allocationRulePageSize: 0,
  loadingCriteriaAttributes: false,
  criteriaAttributes: [],
  loadingBulkAllocations: false,
  bulkAllocations: [],
  bulkDeAllocations: [],
};

const allocationSlice = createSlice({
  name: 'allocation',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchAllocations.pending, state => {
        state.allocations = [];
        state.allocationViewModels = [];
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllocations.fulfilled, (state, action) => {
        state.loading = false;
        state.allocations = [...action.payload];
        state.allocationViewModels = mapAllocationsToViewModels(action.payload);
        // state.page = action.payload.pageNumber;
        // state.pageSize = action.payload.numberOfItems;
        // state.totalItems = action.payload.totalItems;
      })
      .addCase(fetchAllocations.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || 'Failed to fetch Allocations';
      })
      .addCase(fetchAllocationsMetaData.pending, state => {
        state.allocationsMetaData = [];
        state.totalItemsOfMetaData = 0;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllocationsMetaData.fulfilled, (state, action) => {
        state.loading = false;
        state.allocationsMetaData = [...action.payload.records];
        state.totalItemsOfMetaData = action.payload.totalItems;
        // state.page = action.payload.pageNumber;
        // state.pageSize = action.payload.numberOfItems;
      })
      .addCase(fetchAllocationsMetaData.rejected, (state, action) => {
        state.allocationsMetaData = [];
        state.totalItemsOfMetaData = 0;
        state.loading = false;
        state.error =
          (action.payload as string) || 'Failed to fetch Allocations';
      })
      .addCase(reallocateAllocations.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(reallocateAllocations.fulfilled, (state, action) => {
        state.loading = false;
        const updatedAllocations = state.allocations.map(existingAllocation => {
          const updatedAllocation = action.payload.find(updatedAllocation => {
            return updatedAllocation.id === existingAllocation.id;
          });
          return updatedAllocation ? updatedAllocation : existingAllocation;
        });
        state.allocationViewModels =
          mapAllocationsToViewModels(updatedAllocations);
      })
      .addCase(reallocateAllocations.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || 'Failed to reallocate Allocations';
      })
      .addCase(fetchAllocationStatus.pending, state => {
        state.loadingStatus = true;
        state.error = null;
      })
      .addCase(fetchAllocationStatus.fulfilled, (state, action) => {
        state.loadingStatus = false;
        state.error = null;
        state.allocationStatus = action.payload;
      })
      .addCase(fetchAllocationStatus.rejected, (state, action) => {
        state.loadingStatus = false;
        state.error =
          (action.payload as string) || 'Failed to fetch allocation status';
      })
      .addCase(fetchAllocationRules.pending, state => {
        state.allocationRuleViewModels = [];
        state.loadingAllocationRules = true;
        state.error = null;
      })
      .addCase(fetchAllocationRules.fulfilled, (state, action) => {
        state.loadingAllocationRules = false;
        state.allocationRuleViewModels = [...action.payload.records];
        state.allocationRulePage = action.payload.pageNumber;
        state.allocationRulePageSize = action.payload.numberOfItems;
        state.allocationRuleTotalItems = action.payload.totalItems;
      })
      .addCase(fetchAllocationRules.rejected, (state, action) => {
        state.loadingAllocationRules = false;
        state.error =
          (action.payload as string) || 'Failed to fetch Allocations';
      })
      .addCase(fetchCriteriaAttributes.pending, state => {
        state.criteriaAttributes = [];
        state.loadingCriteriaAttributes = true;
        state.error = null;
      })
      .addCase(fetchCriteriaAttributes.fulfilled, (state, action) => {
        state.criteriaAttributes = action.payload;
        state.loadingCriteriaAttributes = false;
      })
      .addCase(fetchCriteriaAttributes.rejected, (state, action) => {
        state.loadingCriteriaAttributes = false;
        state.error =
          (action.payload as string) || 'Failed to fetch attributes';
      })
      .addCase(createRule.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRule.fulfilled, state => {
        state.loading = false;
      })
      .addCase(createRule.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Failed to Create Rule';
      })
      .addCase(fetchBulkAllocations.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBulkAllocations.fulfilled, (state, action) => {
        state.loading = false;
        state.bulkAllocations = [...action.payload];
      })
      .addCase(fetchBulkAllocations.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Failed to Create Rule';
      })
      .addCase(fetchBulkDeAllocations.pending, state => {
        state.bulkDeAllocations = [];
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBulkDeAllocations.fulfilled, (state, action) => {
        state.loading = false;
        state.bulkDeAllocations = [...action.payload];
      })
      .addCase(fetchBulkDeAllocations.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Failed to Create Rule';
      })
      .addCase(bulkAllocate.pending, state => {
        state.loading = true;
        state.uploadingBulkFile = true;
        state.error = null;
      })
      .addCase(bulkAllocate.fulfilled, state => {
        state.loading = false;
        state.uploadingBulkFile = false;
      })
      .addCase(bulkAllocate.rejected, (state, action) => {
        state.loading = false;
        state.uploadingBulkFile = false;
        state.error = (action.payload as string) || 'Failed to Bulk Allocate';
      })
      .addCase(bulkDeAllocate.pending, state => {
        state.loading = true;
        state.uploadingBulkFile = true;
        state.error = null;
      })
      .addCase(bulkDeAllocate.fulfilled, state => {
        state.loading = false;
        state.uploadingBulkFile = false;
      })
      .addCase(bulkDeAllocate.rejected, (state, action) => {
        state.loading = false;
        state.uploadingBulkFile = false;
        state.error = (action.payload as string) || 'Failed to Bulk Allocate';
      });
  },
});

export default allocationSlice;

export const getAllocations = (state: RootState) => state.allocations;

function mapAllocationsToViewModels(
  allocations: Allocation[]
): AllocationViewModel[] {
  const viewModels: AllocationViewModel[] = allocations.map(item => {
    const viewModel: AllocationViewModel = {
      id: item.id,
      loanAccountNumber: item.loanAccountId,
      customerName: '-',
      contactNumber: '-',
      address: '-',
      agentName: item.agentCode,
      branchName: item.branch,
    };
    return viewModel;
  });

  return viewModels;
}
